import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GameSoundGroup, SoundName } from '@dev-fast/types';
import { Subject } from 'rxjs';
import { tap, throttleTime } from 'rxjs/operators';

import { type AudioController, SoundService } from '@app/core/sound-service';

/** РџСЂРѕРґРѕР»Р¶РёС‚РµР»СЊРЅРѕСЃС‚СЊ РјСѓР·С‹РєРё Opening */
const OPENING_MUSIC_DURATION = 17_000;
const APRIL_MADNESS_SOUND_GROUP = GameSoundGroup.ICE_BREAKERS_GROUP;
const TEAR_SOUNDS: SoundName[] = [
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_1,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_2,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_3,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_4,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_5,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_6,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_7,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_8,
  SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_9,
];

@Injectable({
  providedIn: 'root',
})
export class AprilMadnessSoundsService {
  readonly #soundService = inject(SoundService);
  readonly #destroyRef = inject(DestroyRef);

  readonly #activeAudio: AudioController | null | undefined = null;
  readonly #throttledSoundSubject = new Subject<{ soundName: SoundName; fadeCurrentAudio?: boolean }>();

  #openingMusicTimeoutId: ReturnType<typeof setTimeout> | null = null;
  #isOpeningMusicPlaying = false;

  constructor() {
    this.#destroyRef.onDestroy(() => {
      this.#clearOpeningMusicTimeout();
    });

    this.#throttledSoundSubject
      .pipe(
        throttleTime(300),
        tap(({ soundName, fadeCurrentAudio }) => {
          this.playSound(soundName, fadeCurrentAudio);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  init(): void {
    this.#soundService.initModuleSounds(APRIL_MADNESS_SOUND_GROUP);
  }

  playSound(soundName: SoundName, fadeCurrentAudio?: boolean): void {
    if (fadeCurrentAudio) {
      this.#fadeCurrentAudio();
    }
    this.#soundService.playAudio(APRIL_MADNESS_SOUND_GROUP, soundName);
  }

  playSoundThrottled(soundName: SoundName, fadeCurrentAudio?: boolean): void {
    this.#throttledSoundSubject.next({ soundName, fadeCurrentAudio });
  }

  stopSound(soundName: SoundName): void {
    this.#soundService.stopAudio(APRIL_MADNESS_SOUND_GROUP, soundName);
  }

  fadeCurrentAudio(activeAudio: AudioController): void {
    this.#soundService.fadeAudio(activeAudio, { from: 1, to: 0, duration: 100 });
  }

  stopAll(): void {
    this.#soundService.stopAudioGroup(APRIL_MADNESS_SOUND_GROUP);
  }

  muteSounds(): void {
    this.#soundService.muteAudioGroup(APRIL_MADNESS_SOUND_GROUP, true);
  }

  unmuteSounds(): void {
    this.#soundService.muteAudioGroup(APRIL_MADNESS_SOUND_GROUP, false);
  }

  playOpeningMusic(): void {
    this.#clearOpeningMusicTimeout();

    if (!this.#isOpeningMusicPlaying) {
      this.stopSound(SoundName.ICE_BREAKER_MUSIC_LOOP);
      this.stopSound(SoundName.ICE_BREAKER_MUSIC_OPENING);
      this.playSound(SoundName.ICE_BREAKER_MUSIC_OPENING);
      this.#isOpeningMusicPlaying = true;
    }

    this.#openingMusicTimeoutId = setTimeout(() => {
      this.#returnToMusicLoop();
    }, OPENING_MUSIC_DURATION);
  }

  playTearSound(index: number): void {
    if (index >= 0 && index < TEAR_SOUNDS.length) {
      this.playSound(TEAR_SOUNDS[index]);
    }
  }

  playTearFinishSound(): void {
    this.playSound(SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_FINISH);
  }

  playOpenButtonSound(): void {
    this.playSound(SoundName.ICE_BREAKER_OPEN_THE_PACK_BUTTON_OPEN);
  }

  playShowPackSound(): void {
    this.playSound(SoundName.ICE_BREAKER_OPEN_THE_PACK_SHOW_PACK);
  }

  playTurnCardSound(): void {
    this.playSound(SoundName.ICE_BREAKER_OPEN_THE_PACK_TEAR_TURN_CARD);
  }

  playToCollectionButtonSound(): void {
    this.playSound(SoundName.ICE_BREAKER_OPEN_THE_PACK_BUTTON_TO_THE_COLLECTION);
  }

  playWeaponChangeSound(): void {
    this.playSound(SoundName.ICE_BREAKER_OPEN_THE_PACK_BUTTON_WEAPON_CHANGE);
  }

  #returnToMusicLoop(): void {
    this.stopSound(SoundName.ICE_BREAKER_MUSIC_OPENING);
    this.stopSound(SoundName.ICE_BREAKER_MUSIC_LOOP);
    this.playSound(SoundName.ICE_BREAKER_MUSIC_LOOP);
    this.#openingMusicTimeoutId = null;
    this.#isOpeningMusicPlaying = false;
  }

  #clearOpeningMusicTimeout(): void {
    if (this.#openingMusicTimeoutId !== null) {
      clearTimeout(this.#openingMusicTimeoutId);
      this.#openingMusicTimeoutId = null;
    }
  }

  #fadeCurrentAudio(duration = 50): void {
    if (this.#activeAudio) {
      this.#soundService.fadeAudio(this.#activeAudio, { from: 1, to: 0, duration });
    }
  }
}
